<template>
  <div id="funnel" class="widget widget-height">
    <div class="title row-block">
      <h1>{{ $tr('analytics.widgets.funnel.title') }}</h1>

      <div class="widget-settings" @click="toggleSettingModal"></div>

      <img
        v-tooltip="{
          content: $tr('analytics.widgets.funnel.helper'),
          placement: 'auto',
          classes: 'tight'
        }"
        class="canvas-filter"
        src="/static/images/elements/icon_info.svg"
        alt="Info"
      />

      <img class="canvas-filter" src="/static/images/elements/icon_download.svg" alt="Download" @click="saveAsPng" />
    </div>

    <div class="main-content">
      <div v-if="loading" class="spinner-wraper">
        <div class="spinner"></div>
      </div>

      <template v-else>
        <div v-if="emptyData" class="placeholder">
          <img src="/static/images/elements/analytics/widget-start.svg" alt="No data" />

          <p>{{ $tr('analytics.widgets.funnel.noData') }}</p>
        </div>

        <ul v-else class="statuses">
          <li v-for="status in statuses" :key="status.name + status.priority" class="row-block">
            <span class="status-name">{{ status.name }}</span>
            <div class="row-block left">
              <div class="line-wrapper">
                <span class="line" :style="{ width: get_percent(status.percent) + '%' }"></span>
              </div>
              <div class="status">
                <span class="status-count">{{ status.count }}</span>
                <span class="status-percent">{{ get_percent(status.percent) }}%</span>
              </div>
            </div>
          </li>
        </ul>

        <div v-if="selectedList.length < 1" class="statuses-settings">
          <img src="/static/images/elements/analytics/vector2.svg" alt="" />
          <div class="statuses-settings__text">{{ $tr('analytics.widgets.funnel.settingStatuses') }}</div>
        </div>
      </template>
    </div>

    <transition name="modal">
      <settings-modal
        v-if="settingModal"
        :all-statuses="allStatuses"
        :selected-list="selectedList"
        @saveStatuses="saveStatuses"
        @cancelStatuses="cancelStatuses"
      />
    </transition>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import mixinSaveToPng from './mixinSaveToPng.js';
import SettingsModal from './SettingsModal.vue';
// import Modal from '@/components/Modal.vue';

export default {
  name: 'WidgetFunnel',

  components: {
    SettingsModal
    // Modal
  },

  mixins: [mixinSaveToPng],

  props: {
    filtersParams: Object
  },

  data() {
    return {
      settingModal: false
    };
  },

  computed: {
    statuses() {
      const copyStatuses = this.$store.getters['analytics/funnel/statuses'].slice();

      return copyStatuses.sort((a, b) => a.priority - b.priority);
    },

    allStatuses() {
      return this.$store.getters['analytics/funnel/allStatuses'];
    },

    selectedList() {
      return this.$store.getters['analytics/funnel/selectedList'];
    },

    emptyData() {
      return this.statuses ? this.statuses.every(s => s.count === 0) : true;
    },

    ...mapGetters({
      loading: 'analytics/funnel/loading'
    })
  },

  watch: {
    filtersParams() {
      this.fetch();
    }
  },

  beforeMount() {
    this.fetch();
    this.fetchStatuses();
  },

  methods: {
    fetch() {
      this.$store.dispatch('analytics/funnel/fetch', this.filtersParams);
    },

    fetchStatuses() {
      this.$store.dispatch('analytics/funnel/fetchStatuses');
    },

    saveAsPng() {
      this.mixinSaveAsPng({
        filename: `funnel_${this.filtersParams.created_after}_${this.filtersParams.created_before}`,
        saveId: 'funnel',
        filterClass: 'canvas-filter'
      });
    },

    saveStatuses() {
      this.fetch();
      this.toggleSettingModal();
    },

    cancelStatuses() {
      this.toggleSettingModal();
    },

    toggleSettingModal() {
      this.settingModal = !this.settingModal;
    },

    get_percent(percent) {
      if (!percent) {
        return 0;
      }

      return Number(percent);
    }
  }
};
</script>

<style lang="scss" scoped>
.widget .main-content {
  position: relative;

  .description {
    margin-top: 6px;
    min-height: 48px;
  }

  .statuses {
    margin: 0 0 20px;
    display: flex;
    flex-direction: column;

    .row-block {
      margin: 0 0 25px;
      width: 100%;
      display: flex;

      &:first-child {
        margin-top: 10px;
      }
      &:last-child {
        margin-bottom: 0;
      }

      span {
        line-height: 22px;
        font-size: 15px;
      }

      .status-name {
        display: block;
        width: 200px;
        margin-right: 55px;
      }

      .row-block.left {
        margin: 0;
        width: auto;
        justify-content: flex-start;
        flex: 1;

        .status {
          display: flex;
          flex: 1;
          align-items: center;
          justify-content: space-between;
          white-space: nowrap;
          line-height: 18px;
          font-size: 15px;
          color: #607183;
        }

        .status-count {
          display: block;
          min-width: 42px;
          margin-right: 7px;
          font-weight: 500;
          line-height: 22px;
          font-size: 15px;
          letter-spacing: 0.005em;
          color: #233346;
        }

        .status-percent {
          font-weight: 300;
        }

        .line-wrapper {
          position: relative;
          width: 102px;
          margin-right: 49px;
        }

        .line {
          display: block;
          height: 15px;
          background: #69c758;
          min-width: 2px;
        }
      }
    }

    &-settings {
      display: flex;
      flex-direction: column;
      align-items: center;
      position: absolute;
      top: 0;
      bottom: 0;
      left: 0;
      right: 0;
      background: #fff;
      padding-top: 44px;

      &__text {
        font-size: 15px;
        line-height: 22px;
        color: #233346;
        margin-top: 44px;
      }
    }
  }

  .placeholder {
    text-align: center;

    img {
      margin: 42px auto 54px;
    }
  }

  .spinner-wraper {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100px;
  }

  .spinner {
    margin: auto;
    height: 50px;
    width: 50px;
    top: 0;
  }
}

.widget {
  &-settings {
    width: 18px;
    height: 18px;
    background: url('/static/images/elements/icon_settings.svg') center no-repeat;
    margin-left: auto;
    cursor: pointer;
  }
}
</style>
